import { ConnectionStates, StreamStates } from "@minervaproject/avsubsystem";
import { palette } from "@minervaproject/fds";
import clsx from "clsx";
import cuid from "cuid";
import { ChangeEvent, useState } from "react";
import { UAParser } from "ua-parser-js";

import { TD, TDStatusIconWithTooltip, TH } from "./table";

import { PreclassCheckMessages } from "components/local_modal/preclass_check/preclass_check_messages";
import { SignalsAndReactions } from "components/shared/signals_and_reactions";
import {
  useAppDispatch,
  useAppSelector,
  useArraySelector,
  useObjectSelector,
} from "hooks/redux_hooks";
import { useUserProperties } from "hooks/use_user_properties";
import { Settings } from "settings";
import {
  DevicesSelectedMonitoringEventType,
  EnterClassMonitoringEventType,
  MonitoringEventTypeType,
} from "shared/types/monitoring_event";
import {
  LatestPreclassCheckResultType,
  PreclassTestNameType,
  PreclassTestOutcomeType,
} from "shared/types/preclass_check";
import { BandwidthModeType } from "shared/types/user";
import { buildDatadogFacetedUrl, getClassDurationInMinutes } from "shared/utils/monitoring";
import { classroomSelectors, globalSelectors } from "store";
import { setMonitoringEventTableUsers } from "store/reducers/local_monitoring";
import { selectEffectivePttMode } from "store/selectors/classroom";
import { selectIntercomConversationCountByUserId } from "store/selectors/local_monitoring";
import {
  selectAvRTTForUser,
  selectAvRefreshCountByUserId,
  selectLatestMonitoringEventByUserIdAndType,
  selectLatestPreClassCheckResultsByUserId,
  selectLatestPubsubStatsByUserId,
  selectMonitoringEventCountByTargetUserIdAndType,
  selectMonitoringEventCountByUserIdAndType,
  selectSecondsWithLowQualityByUserId,
  selectSecondsWithPoorConnectionIndicatorByUserId,
  selectSupportActionCountByUserId,
} from "store/selectors/monitoring";
import { deviceErrorForDisplay } from "utils/av_utils";
import { formatTime } from "utils/date";
import { longDisplayName, talkTimeForDisplay } from "utils/user";

const PRECLASS_CHECK_MESSAGES = new PreclassCheckMessages();

function getBandwidthLabel(bandwidthMode: BandwidthModeType) {
  const bwModeToLabel: { [key: string]: string } = {
    high: "automatic",
    low: "limited",
  };
  return bwModeToLabel[bandwidthMode] || bandwidthMode;
}

type UserTypeFilterPropsType = {
  filterValue: string[];
  setFilterValue: (filterValue: string[]) => void;
};

function UserTypeFilter({ filterValue, setFilterValue }: UserTypeFilterPropsType) {
  const onFilterChange = (userType: string) => (event: ChangeEvent) => {
    const { checked } = event.target as HTMLInputElement;
    const newFilter = checked
      ? [...filterValue, userType]
      : filterValue.filter((type) => type !== userType);
    setFilterValue(newFilter);
  };

  return (
    <div id="usertype-drowpdown-filter">
      <ul className="p3 mb4" style={{ border: "1px solid #dee2e6" }}>
        <li className="inline pr5">User Filter: </li>
        <li className="inline pr5">
          <input
            type="checkbox"
            className="form-check-input"
            id="usertype-present"
            checked={filterValue.includes("PRESENT")}
            onChange={onFilterChange("PRESENT")}
          />
          <label className="form-check-label" htmlFor="usertype-present">
            Present Participants
          </label>
        </li>
        <li className="inline pr5">
          <input
            type="checkbox"
            className="form-check-input"
            id="usertype-absent"
            checked={filterValue.includes("ABSENT")}
            onChange={onFilterChange("ABSENT")}
          />
          <label className="form-check-label" htmlFor="usertype-absent">
            Absent Participants
          </label>
        </li>
        <li className="inline pr5">
          <input
            type="checkbox"
            className="form-check-input"
            id="usertype-observers"
            checked={filterValue.includes("OBSERVERS")}
            onChange={onFilterChange("OBSERVERS")}
          />
          <label className="form-check-label" htmlFor="usertype-observers">
            Observers
          </label>
        </li>
      </ul>
    </div>
  );
}

function UserStatsHeader() {
  return (
    <thead>
      <tr>
        <TH> </TH>
        <TH>User ID</TH>
        <TH>User Name</TH>
        <TH>Role</TH>
        <TH title="Initial enter time">Enter</TH>
        <TH title="Number of minutes present in the class">Presence</TH>
        <TH preWrap>{`Breakout\nRoom`}</TH>
        <TH title="Talktime (in seconds) in the main classroom and in breakouts">Talktime</TH>
        <TH preWrap>{`Bug\nReports`}</TH>
        <TH preWrap>{`Intercom\nConversations`}</TH>
        <TH preWrap>{`Page\nLoads`}</TH>
        <TH
          title="Number of AV refreshes (excluding update-service-info and av-service-requested)"
          preWrap
        >
          {`AV\nRefreshes`}
        </TH>
        <TH title="Number of stream reloads (user initiated / user targeted)" preWrap>
          {`Stream\nReloads`}
        </TH>
        <TH preWrap>{`Support\nActions`}</TH>
        <TH>Ping</TH>
        <TH
          title="Number of seconds where Poor Connection indicator was visible"
          preWrap
        >{`Poor\nConnection`}</TH>
        <TH>A/V</TH>
        <TH title="Results of latest pre-class checks" preWrap>
          {`Pre-class\nChecks`}
        </TH>
        <TH title="Bandwidth Mode">BW</TH>
        <TH title="Round trip time from AV server">RTT</TH>
        <TH
          title="Number of seconds where the A/V service reported Low Quality"
          preWrap
        >{`Low AV\nQuality`}</TH>
        <TH title="Push-to-talk">PTT</TH>
        <TH>IP</TH>
        <TH>Browser</TH>
        <TH>OS</TH>
      </tr>
    </thead>
  );
}

function UserStatsRow({
  classId,
  userId,
  duration,
}: {
  classId: string;
  userId: string;
  duration: number | null;
}) {
  const dispatch = useAppDispatch();
  const user = useUserProperties(userId, [
    "bandwidth_mode",
    // TODO: verify that this works with av given that it is nested
    "av",
    "is_speaking",
    "is_present",
    "role",
    "classroom_talk_time",
    "breakout_talk_time",
    "seconds_present",
    "enter_class_time",
  ]);

  const isAudioOnly =
    user?.bandwidth_mode === BandwidthModeType.AudioOnly ||
    user?.bandwidth_mode === BandwidthModeType.AudioOnlyForced;

  const pttMode = useAppSelector(selectEffectivePttMode(userId));

  const enterClassCount = useAppSelector(
    selectMonitoringEventCountByUserIdAndType(userId, MonitoringEventTypeType.ENTER_CLASS),
  );

  const bugReportCount = useAppSelector(
    selectMonitoringEventCountByUserIdAndType(userId, MonitoringEventTypeType.BUG_REPORT_SUBMITTED),
  );
  const intercomConversationCount = useAppSelector(selectIntercomConversationCountByUserId(userId));

  const pageLoadCount = useAppSelector(
    selectMonitoringEventCountByUserIdAndType(userId, MonitoringEventTypeType.ENTER_CLASS),
  );
  const normalizedPageLoadCount = (duration && pageLoadCount / (duration / 60)) || 0;

  const avRefreshCount = useAppSelector(selectAvRefreshCountByUserId(userId));
  const normalizedAvRefreshCount = (duration && avRefreshCount / (duration / 60)) || 0;

  const streamReloadCount = useAppSelector(
    selectMonitoringEventCountByUserIdAndType(userId, MonitoringEventTypeType.RELOAD_STREAM),
  );
  const targetStreamReloadCount = useAppSelector(
    selectMonitoringEventCountByTargetUserIdAndType(userId, MonitoringEventTypeType.RELOAD_STREAM),
  );

  const supportActionCount = useAppSelector(selectSupportActionCountByUserId(userId));

  const pubsubStats = useAppSelector(selectLatestPubsubStatsByUserId(userId));
  const pingRTT = pubsubStats?.contextData.pingRTT;
  const medianPingRTT = pingRTT?.median || 0;
  const formattedPingRTT = pingRTT ? `(${pingRTT.min}, ${pingRTT.median}, ${pingRTT.max})` : "";

  const latestEnterClassEvent = useObjectSelector(
    selectLatestMonitoringEventByUserIdAndType(userId, MonitoringEventTypeType.ENTER_CLASS),
    ["id"],
  ) as EnterClassMonitoringEventType | undefined;
  const { ipAddress, userAgent } = latestEnterClassEvent?.contextData || {};
  const parser = userAgent && new UAParser(userAgent);
  const parserResults = parser && parser.getResult();
  const { browser, os } = parserResults || {};

  const activeBreakoutSessionId = useAppSelector(
    classroomSelectors.selectActiveBreakoutSessionId(),
  );
  const breakoutRoom = useAppSelector(
    classroomSelectors.selectAssignedBreakoutRoomByUserId(userId),
  );
  const visitingBreakoutRoom = useAppSelector(
    classroomSelectors.selectVisitingBreakoutRoomByUserId(userId),
  );
  const breakoutRoomText = activeBreakoutSessionId
    ? breakoutRoom
      ? `Group ${breakoutRoom?.title}`
      : visitingBreakoutRoom
        ? `Visiting Group ${visitingBreakoutRoom?.title}`
        : "Control Room"
    : "N/A";

  const cameraError = user?.av.camera_error;
  const connectionState = user?.av.connection_state;
  const micError = user?.av.mic_error;
  const publishedStreamState = user?.av.published_stream_state;
  const avIssue =
    !!micError ||
    !!cameraError ||
    connectionState === ConnectionStates.failed ||
    publishedStreamState === StreamStates.failed;
  const latestDevicesSelectedEvent = useObjectSelector(
    selectLatestMonitoringEventByUserIdAndType(userId, MonitoringEventTypeType.DEVICES_SELECTED),
    ["id"],
  ) as DevicesSelectedMonitoringEventType | undefined;
  const devices = latestDevicesSelectedEvent?.contextData?.devicesState?.device_list || [];
  const currentCamera =
    devices.find((device: { kind: string }) => device.kind === "videoinput")?.label || "";
  const currentMic =
    devices.find((device: { kind: string }) => device.kind === "audioinput")?.label || "";
  const currentSpeaker = devices.find((device) => device.kind === "audiooutput")?.label || "";

  const secondsWithPoorConnectionIndicator = useAppSelector(
    selectSecondsWithPoorConnectionIndicatorByUserId(userId),
  );
  const secondsWithLowQuality = useAppSelector(selectSecondsWithLowQualityByUserId(userId));

  const latestPreClassCheckResults = useObjectSelector(
    selectLatestPreClassCheckResultsByUserId(userId),
    ["eventId"],
  ) as LatestPreclassCheckResultType;
  const preClassCheckOutcome = latestPreClassCheckResults.outcome;
  const preClassTests = latestPreClassCheckResults.tests;
  const preClassCheckFailed = preClassTests
    .filter(({ name }) => name !== PreclassTestNameType.EXTENSION)
    .some(({ outcome }) =>
      [
        PreclassTestOutcomeType.FAIL,
        PreclassTestOutcomeType.ABORT,
        PreclassTestOutcomeType.EXCEPTION,
      ].includes(outcome),
    );
  const preClassCheckMessage =
    PRECLASS_CHECK_MESSAGES.getHighestPriorityMessageFromResults(
      preClassTests.reduce((obj, val) => ({ ...obj, [val.name]: val }), {}),
    )?.message || "";

  const avRTT = useAppSelector(selectAvRTTForUser(userId));

  const getInitialEnterTime = () => {
    if (user?.enter_class_time || enterClassCount === 0) {
      return formatTime(user?.enter_class_time);
    }
    // TODO: after this has been in production for 3 months, we can remove the reference to Django Admin
    // See https://app.asana.com/0/1204874309761290/1209230791633277
    return "See Django Admin";
  };

  if (!user) {
    return null;
  }

  return (
    <tr data-testid={`user-stats-${userId}`}>
      <TD>
        <div className="flex items-center" style={{ width: "80px" }}>
          <div
            style={{
              width: "16px",
              height: "16px",
              borderRadius: "8px",
              backgroundColor: user.is_speaking ? palette.green : undefined,
            }}
            className="my1"
          />
          <div
            className="relative"
            style={{
              width: "24px",
              height: "24px",
            }}
          >
            <SignalsAndReactions userId={userId} size="sm" noAnimation />
          </div>
          <span
            className="mr1 cursor-pointer"
            role="img"
            aria-label="Link to monitoring events"
            onClick={() => {
              dispatch(setMonitoringEventTableUsers([userId]));
              document.getElementById("monitoring-events-section")?.scrollIntoView();
            }}
          >
            🔍
          </span>
          <a href={buildDatadogFacetedUrl({ classId, userId })} target="_blank" rel="noreferrer">
            🐕
          </a>
        </div>
      </TD>
      <TD>
        <a target="_blank" rel="noreferrer" href={`/monitoring/classes/${classId}/users/${userId}`}>
          {userId}
        </a>
      </TD>
      <TD dataTestId={`user-stats-${userId}-name`}>
        <span className={clsx({ bold: user.is_present })}>{longDisplayName(user)}</span>
      </TD>
      <TD dataTestId={`user-stats-${userId}-role`}>{user.role || "-"}</TD>
      <TD dataTestId={`user-stats-${userId}-enter-time`}>{getInitialEnterTime()}</TD>
      <TD dataTestId={`user-stats-${userId}-seconds-present`}>{`${Math.round(
        (user.seconds_present || 0) / 60,
      )}`}</TD>
      <TD dataTestId={`user-stats-${userId}-breakout-room`}>{breakoutRoomText}</TD>
      <TD dataTestId={`user-stats-${userId}-talktime`}>
        {`${talkTimeForDisplay(user.classroom_talk_time)}/${talkTimeForDisplay(
          user.breakout_talk_time,
        )}`}
      </TD>
      <TD
        dataTestId={`user-stats-${userId}-bug-reports`}
        conditionForDanger={bugReportCount >= 1}
      >{`${bugReportCount}`}</TD>
      <TD
        dataTestId={`user-stats-${userId}-intercom-conversations`}
        conditionForWarning={intercomConversationCount >= 1}
      >{`${intercomConversationCount}`}</TD>
      <TD
        dataTestId={`user-stats-${userId}-page-loads`}
        conditionForDanger={pageLoadCount > 1 && normalizedPageLoadCount >= 2}
        conditionForWarning={pageLoadCount > 1 && normalizedPageLoadCount > 1}
      >{`${pageLoadCount}`}</TD>
      <TD
        dataTestId={`user-stats-${userId}-av-refreshes`}
        conditionForDanger={normalizedAvRefreshCount >= 2}
        conditionForWarning={avRefreshCount > 1}
      >
        {`${avRefreshCount}`}
      </TD>
      <TD
        dataTestId={`user-stats-${userId}-stream-reload`}
        noWrap
        conditionForDanger={streamReloadCount >= 3 || targetStreamReloadCount >= 3}
        conditionForWarning={streamReloadCount > 1 || targetStreamReloadCount > 1}
      >
        {`${streamReloadCount} / ${targetStreamReloadCount}`}
      </TD>
      <TD
        dataTestId={`user-stats-${userId}-support-actions`}
        conditionForDanger={supportActionCount >= 1}
      >{`${supportActionCount}`}</TD>

      {/* Pubsub */}
      <TD
        dataTestId={`user-stats-${userId}-ping-rtt`}
        conditionForDanger={medianPingRTT >= Settings.PUBSUB_PING_DANGER_THRESHOLD}
        conditionForWarning={medianPingRTT >= Settings.PUBSUB_PING_WARNING_THRESHOLD}
      >
        {formattedPingRTT}
      </TD>
      <TD
        dataTestId={`user-stats-${userId}-poor-connection`}
      >{`${secondsWithPoorConnectionIndicator}`}</TD>

      {/* A/V */}

      <TDStatusIconWithTooltip
        dataTestId={`user-stats-${userId}-av`}
        conditionForDanger={avIssue}
        conditionForWarning={isAudioOnly}
      >
        {connectionState && (
          <div className="text-black">
            {cameraError && (
              <div>
                Camera Error: <span className="text-red">{deviceErrorForDisplay(cameraError)}</span>
              </div>
            )}
            {micError && (
              <div>
                Mic Error: <span className="text-red">{deviceErrorForDisplay(micError)}</span>
              </div>
            )}
            <div>
              Connection state:{" "}
              <span
                className={clsx({
                  "text-red": connectionState === ConnectionStates.failed,
                  "text-green": connectionState === ConnectionStates.connected,
                })}
              >
                {connectionState}
              </span>
            </div>
            <div>
              Published stream state:{" "}
              <span
                className={clsx({
                  "text-red": publishedStreamState === StreamStates.failed,
                  "text-green": publishedStreamState === StreamStates.playable,
                })}
              >
                {publishedStreamState || "No Published Stream"}
              </span>
            </div>
            <div className="mt2">
              Camera: <span className="text-green">{currentCamera}</span>
            </div>
            <div>
              Mic: <span className="text-green">{currentMic}</span>
            </div>
            <div>
              Speaker: <span className="text-green">{currentSpeaker}</span>
            </div>
            <div className="mt2">
              Bandwidth Mode:{" "}
              <span
                className={clsx({
                  "text-red": isAudioOnly,
                  "text-green": !isAudioOnly,
                })}
              >
                {getBandwidthLabel(user.bandwidth_mode)}
              </span>
            </div>
          </div>
        )}
      </TDStatusIconWithTooltip>
      <TDStatusIconWithTooltip
        dataTestId={`user-stats-${userId}-preclass-checks`}
        conditionForDanger={preClassCheckFailed}
        conditionForWarning={preClassCheckOutcome === "fail"}
      >
        {preClassCheckOutcome && (
          <div className="text-black">
            {preClassTests.map((test) => (
              <div key={cuid()}>
                {test.category} / {test.name}:{" "}
                <span
                  className={clsx({
                    "text-green": test.outcome === PreclassTestOutcomeType.SUCCESS,
                    "text-red": ![
                      PreclassTestOutcomeType.SUCCESS,
                      PreclassTestOutcomeType.NA,
                      PreclassTestOutcomeType.IN_PROGRESS,
                    ].includes(test.outcome),
                  })}
                >
                  {test.outcome}
                </span>
              </div>
            ))}
            {preClassCheckMessage && (
              <div className="mt2">
                <div>Highest priority error message:</div>
                <div className="text-red">{preClassCheckMessage}</div>
              </div>
            )}
          </div>
        )}
      </TDStatusIconWithTooltip>
      <TDStatusIconWithTooltip
        dataTestId={`user-stats-${userId}-bandwidth-mode`}
        conditionForDanger={isAudioOnly}
        conditionForWarning={user.bandwidth_mode === BandwidthModeType.LimitedBandwidth}
      >
        <div className="text-black">Bandwidth Mode: {getBandwidthLabel(user.bandwidth_mode)}</div>
      </TDStatusIconWithTooltip>
      <TD dataTestId={`user-stats-${userId}-av-rtt`}>{avRTT || "-"}</TD>
      <TD dataTestId={`user-stats-${userId}-low-quality`}>{`${secondsWithLowQuality}`}</TD>
      <TD dataTestId={`user-stats-${userId}-ptt`}>{pttMode ? "Y" : "N"}</TD>

      {/* IP, Browser, OS */}
      <TD dataTestId={`user-stats-${userId}-ip-address`}>{ipAddress || "-"}</TD>
      <TD dataTestId={`user-stats-${userId}-browser`}>
        {browser ? `${browser.name} ${browser.version}` : "-"}
      </TD>
      <TD dataTestId={`user-stats-${userId}-os`}>{os ? `${os.name} ${os.version}` : "-"}</TD>
    </tr>
  );
}

function UserStatsBody({ filterValue }: { filterValue: string[] }) {
  const allClassUserIds = useArraySelector(globalSelectors.selectAllClassUserIds(filterValue));
  const classInfo = useAppSelector(classroomSelectors.selectClassInfo());
  const duration = getClassDurationInMinutes(classInfo);
  return (
    <tbody>
      {allClassUserIds.map((userId) => (
        <UserStatsRow classId={classInfo.id} userId={userId} duration={duration} key={userId} />
      ))}
    </tbody>
  );
}

function UserStats() {
  const [filterValue, setFilterValue] = useState(["PRESENT"]);

  return (
    <div>
      <UserTypeFilter filterValue={filterValue} setFilterValue={setFilterValue} />
      <table
        className="fds-table table-hover"
        style={{ tableLayout: "auto" }}
        data-testid="user-stats-table"
      >
        <UserStatsHeader />
        <UserStatsBody filterValue={filterValue} />
      </table>
    </div>
  );
}

export { UserTypeFilter, UserStatsHeader, UserStatsRow, UserStatsBody, UserStats };
